import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/lenis";
import { IMAGES } from "@/lib/images";

const LINES = [
    <>Redécouvrez le plaisir</>,
    <>de vous <em className="italic text-sage">asseoir</em></>,
    <>sans douleur.</>,
];

const FEATURES = [
    "Décharge à 100% le coccyx & le sacrum",
    "Mousse à mémoire de forme haute densité (80 kg/m³)",
    "Housse respirante & lavable en machine",
    "Essai 30 nuits — Satisfait ou Remboursé",
];

const CHIPS = [
    { label: "Découpe en U coccygienne", cls: "left-4 sm:left-8 top-6", anim: "animate-float-slow" },
    { label: "Mousse mémoire 80 kg/m³", cls: "right-4 sm:right-8 bottom-16", anim: "animate-float-slower" },
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const imgR = useTransform(scrollYProgress, [0, 1], [0, 2.5]);

    return (
        <section id="hero" ref={ref} className="relative pt-28 lg:pt-40 pb-16 lg:pb-24 overflow-hidden">
            <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-sage-soft blur-3xl opacity-70 pointer-events-none" />
            <div className="absolute top-40 -right-40 w-[420px] h-[420px] rounded-full bg-terra-soft blur-3xl opacity-60 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                <div className="lg:col-span-6">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.05 }}
                        className="text-xs uppercase font-mono tracking-[0.25em] text-sage-mid"
                    >
                        Coussin Ergonomique Coccyx — Ergocox™
                    </motion.p>
                    <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.06] tracking-tight text-ink">
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block"
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.65 }}
                        className="mt-6 text-base sm:text-lg text-[#4A5550] leading-relaxed max-w-xl"
                    >
                        Le coussin ergonomique de précision médicale conçu pour soulager
                        instantanément la pression du coccyx, redresser la posture et
                        transformer vos longues heures assises.
                    </motion.p>
                    <motion.ul
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                        className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl"
                    >
                        {FEATURES.map((f, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-sm text-ink/80" data-testid={`hero-feature-${i}`}>
                                <span className="mt-0.5 w-4.5 h-4.5 shrink-0 rounded-full bg-sage-soft text-sage grid place-items-center">
                                    <Check size={12} strokeWidth={3} />
                                </span>
                                {f}
                            </li>
                        ))}
                    </motion.ul>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.95 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta"
                            onClick={() => scrollToId("offres")}
                            className="rounded-full bg-sage hover:bg-sage-hover text-cream font-semibold px-8 py-4 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl shadow-sage/25"
                        >
                            Commander maintenant — 49,90 €
                        </button>
                        <button
                            data-testid="hero-secondary-cta"
                            onClick={() => scrollToId("manifeste")}
                            className="inline-flex items-center gap-2 rounded-full border border-sage/25 px-7 py-4 text-sm font-semibold text-sage hover:bg-sage-soft transition-colors"
                        >
                            Découvrir le manifeste <ArrowDown size={16} />
                        </button>
                    </motion.div>
                </div>
                <div className="lg:col-span-6 relative">
                    <motion.div
                        style={{ y: imgY, rotate: imgR }}
                        initial={{ opacity: 0, scale: 0.94 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="relative"
                    >
                        <div className="absolute -inset-10 bg-[radial-gradient(closest-side,rgba(200,138,88,0.28),transparent)] blur-2xl pointer-events-none" />
                        <img
                            data-testid="hero-product-image"
                            src={IMAGES.hero}
                            alt="Coussin ergonomique Ergocox sous lumière directionnelle"
                            className="relative w-full aspect-[4/5] sm:aspect-[5/5] lg:aspect-[5/5.4] object-cover rounded-[2rem] shadow-2xl shadow-sage/25"
                        />
                        {CHIPS.map((c, i) => (
                            <div
                                key={i}
                                className={`absolute ${c.cls} ${c.anim} bg-cream/85 backdrop-blur-md border border-sage/10 rounded-full px-4 py-2 text-xs font-semibold text-sage shadow-lg`}
                            >
                                {c.label}
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
